import React from "react";
//#region types
import { NestedTablePrintRowProps } from "models/propTypes/simpleTableProps";
import { TableEnums } from "models/enums";
//#endregion
//#region components
import { View, Text } from "@react-pdf/renderer";
//#endregion
import nestedTableStyles from "theme/custom/nestedTablePdf";

const NestedTablePrintRow: React.FC<NestedTablePrintRowProps> = (props) => {
  const {
    row,
    index,
    path,
    childrenKey,
    hasChildren: hasChildRows,
    depth,
    repeatProperties,
    columns,
    repeatColumnsLength,
    repeatColumnDefaultWidth,
    nonRepeatColumnWidth,
  } = props;

  const hasChildren = hasChildRows ? hasChildRows(row) : row[childrenKey]?.length;

  const rowDisplayType = props.rowDisplayType ? props.rowDisplayType(row) : undefined;

  const getCellDisplayTypeClass = (repeatIndex: number) => {
    switch (rowDisplayType) {
      case TableEnums.RowDisplayType.SumSmall:
        return nestedTableStyles.sumRow;
      case TableEnums.RowDisplayType.Empty:
        return nestedTableStyles.empty;
      case TableEnums.RowDisplayType.Sum:
        return getColumnSumNegative(repeatIndex) ? nestedTableStyles.sumNegative : nestedTableStyles.sumPositive;
      default:
        return depth === 0
          ? nestedTableStyles.rowLevel1
          : depth === 1
          ? nestedTableStyles.rowLevel2
          : nestedTableStyles.rowLevel3;
    }
  };

  const getColumnSumNegative = (repeatColumnIndex: number) => {
    const amount = repeatProperties ? row[repeatProperties.key][repeatColumnIndex] : undefined;

    return (
      rowDisplayType === TableEnums.RowDisplayType.Sum &&
      repeatProperties?.sumNegative &&
      amount !== undefined &&
      repeatProperties.sumNegative(row[repeatProperties.key][repeatColumnIndex])
    );
  };

  const getTotalCellValue = () => {
    return [...Array(repeatColumnsLength)].reduce(
      (prev: number, current: number, currentIndex: number) =>
        prev +
        (repeatProperties!.showTotal?.valueFormatter
          ? repeatProperties!.showTotal?.valueFormatter(
              row[repeatProperties!.key][currentIndex],
              index,
              depth,
              currentIndex,
              row
            )
          : row[repeatProperties!.key][currentIndex][repeatProperties!.showTotal!.key]),
      0
    );
  };

  return rowDisplayType === TableEnums.RowDisplayType.Empty ? (
    <></>
  ) : rowDisplayType === TableEnums.RowDisplayType.Title ? (
    <View>
      <View style={[nestedTableStyles.row, nestedTableStyles.titleRow]} wrap={false}>
        <Text style={nestedTableStyles.cell}>{row[columns[0][0]]}</Text>
      </View>
    </View>
  ) : (
    <View>
      <View style={nestedTableStyles.row} wrap={false}>
        {columns.map(([key, cellValue], index) => (
          <View
            key={key}
            style={[
              nestedTableStyles.cell,
              getCellDisplayTypeClass(0),
              {
                width: nonRepeatColumnWidth / columns.length,
                textAlign:
                  cellValue?.alignment === undefined || cellValue?.alignment === "inherit"
                    ? "left"
                    : cellValue?.alignment,
              },
            ]}
          >
            <Text
              style={[
                depth === 0
                  ? nestedTableStyles.rowLevel1MainText
                  : depth === 1
                  ? nestedTableStyles.rowLevel2MainText
                  : nestedTableStyles.rowLevel3MainText,
              ]}
            >
              {cellValue?.formatter ? cellValue.formatter(row, index, depth) : row[key]}
            </Text>
          </View>
        ))}
        {[...Array(repeatColumnsLength)].map((col, repeatIndex) => (
          <View key={repeatIndex} style={[nestedTableStyles.flexRow, getCellDisplayTypeClass(repeatIndex)]}>
            {repeatProperties?.properties ? (
              Object.entries(repeatProperties.properties).map(([repeatKey, cellValue], cellIndex) => (
                <Text
                  key={repeatKey}
                  style={[
                    nestedTableStyles.cell,
                    cellValue?.styles ??
                      (!cellIndex ? nestedTableStyles.repeatCellMain : nestedTableStyles.repeatCellSecondary),
                    {
                      width: repeatColumnDefaultWidth,
                      textAlign:
                        cellValue?.alignment === undefined || cellValue?.alignment === "inherit"
                          ? "left"
                          : cellValue?.alignment,
                      fontSize: repeatColumnsLength > 8 ? 7 : undefined,
                    },
                  ]}
                >
                  {cellValue?.formatter
                    ? cellValue.formatter(row[repeatProperties.key][repeatIndex], index, depth, repeatIndex, row)
                    : row[repeatProperties.key][repeatIndex][repeatKey]}
                </Text>
              ))
            ) : (
              <></>
            )}
          </View>
        ))}
        {repeatProperties?.showTotal ? (
          <View
            style={[
              nestedTableStyles.cell,
              getCellDisplayTypeClass(0),
              nestedTableStyles.totalCell,
              { width: repeatColumnDefaultWidth, textAlign: "right" },
            ]}
          >
            <Text style={[nestedTableStyles.repeatCellMain, { fontSize: repeatColumnsLength > 8 ? 7 : undefined }]}>
              {repeatProperties.showTotal?.displayFormatter
                ? repeatProperties.showTotal.displayFormatter(getTotalCellValue())
                : getTotalCellValue()}
            </Text>
          </View>
        ) : (
          <></>
        )}
      </View>
      {hasChildren && rowDisplayType === undefined ? (
        <>
          {row[childrenKey].map((childRow: any, childRowIndex: number) => (
            <NestedTablePrintRow
              {...props}
              key={`tab-row-${depth + 1}-${childRowIndex}`}
              row={childRow}
              index={childRowIndex}
              path={[...path, index]}
              depth={depth + 1}
            />
          ))}
        </>
      ) : (
        <></>
      )}
    </View>
  );
};

export default NestedTablePrintRow;
